import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AnimatedLetters from '../AnimatedLetters';
import './index.scss';

const roles = [
  {
    title: 'Legal Software Engineer',
    company: 'Kreisson Legal',
    period: 'Aug 2025 — Mar 2026',
    status: 'COMPLETED',
    bullets: [
      'Designed and built AI-powered software tools for a specialist legal practice, automating document workflows and reducing time spent on high-volume repetitive tasks.',
      'Architected document retrieval and search systems using vector search and hybrid retrieval techniques, significantly reducing query response time.',
      'Operated across the full stack — backend API design, database architecture, React frontend interfaces — delivering production-ready systems in a resource-constrained environment.',
      'Built distributed compute infrastructure for internal application deployment, eliminating unnecessary cloud costs and enabling on-demand scaling.',
      'Bridged legal domain knowledge with software engineering to translate complex practice requirements into reliable, functional systems.',
    ],
    stack: ['React', 'Python', 'FastAPI', 'RAG', 'Vector DB', 'Hugging Face', 'Ubuntu Server'],
  },
  {
    title: 'Helpdesk / Document Automation',
    company: 'LEAP Legal Software',
    period: 'May 2023 — Dec 2023',
    status: 'COMPLETED',
    bullets: [
      'Automated legal documents using Word and Adobe PDF tools, streamlining document production workflows for law firm clients.',
      'Maintained JavaScript code for e-signature integrations ensuring reliable client-facing functionality.',
      'Interpreted XML and JSON data files to support accurate data management and software configuration.',
      'Managed trust accounting compliance and invoice creation workflows for law firm billing processes.',
    ],
    stack: ['JavaScript', 'XML/JSON', 'Legal Tech', 'Document Automation'],
  },
  {
    title: 'Intern',
    company: 'Lawpath',
    period: 'Nov 2022 — Feb 2023',
    status: 'COMPLETED',
    bullets: [
      'Built a search engine prototype for internal testing purposes.',
      'Reviewed legal documents and contracts to develop understanding of legal tech product requirements.',
      'Presented a portfolio of product ideas directly to the CEO — proposals were subsequently adopted for integration into Lawpath\'s platform.',
    ],
    stack: ['Frontend', 'Backend', 'Legal Tech', 'Product'],
  },
];

const projects = [
  {
    title: 'Horizon Legal Docs',
    level: 'Production — Advanced',
    description:
      'AI-powered legal document automation platform. ' +
      'Full-stack system spanning document ingestion, NLP-driven clause analysis, ' +
      'compliance checking, and automated citation generation. ' +
      'Built with a React frontend, FastAPI backend, and custom transformer pipelines — deployed in production.',
    stack: ['React', 'Python', 'FastAPI', 'NLP', 'Transformers', 'PostgreSQL'],
  },
  {
    title: 'Arbiter Legal AI',
    level: 'Production — Advanced',
    description:
      'Multi-model legal AI research assistant powered by RAG and hybrid vector search. ' +
      'Delivers near-instant answers across large legal document sets. ' +
      'Supports simultaneous queries to multiple LLMs for comparative legal reasoning. ' +
      'Query response time reduced from several minutes to sub-second.',
    stack: ['Python', 'FastAPI', 'RAG', 'Vector DB', 'Hugging Face', 'React', 'LLM Orchestration'],
  },
  {
    title: 'Neural Network Tribe Simulation (DVG)',
    level: 'Research — Advanced',
    description:
      'Agent-based economic simulation in Rust where tribes with evolving neural networks discover chemistry, ' +
      'trade, lend, fight, and invent language. Reference run golden_10k_1: 1.77M trades, 670+ emergent compounds, ' +
      '12,914 loans, 2.1M utterances — macro cycles emerge from local bilateral rules with no central planner.',
    stack: ['Rust', 'Python', 'Agent Simulation', 'Computational Economics', 'Hebbian Learning', 'Event Analysis'],
  },
  {
    title: 'Flask Blockchain',
    level: 'Personal — Advanced',
    description:
      'Full blockchain implementation from scratch in Python and Flask. ' +
      'Proof-of-work consensus, distributed nodes, transaction validation, ' +
      'and a REST API for mining, inspecting the chain, and registering nodes. ' +
      'Built to deeply understand how distributed ledger systems operate at the protocol level.',
    stack: ['Python', 'Flask', 'REST API', 'Cryptography', 'Distributed Systems', 'Solidity'],
  },
];

const services = [
  {
    title: 'Contract Intelligence',
    icon: '§',
    description:
      'Automated clause extraction, risk identification, and compliance review for complex contracts. Custom-built NLP pipelines that cut hours of manual review down to minutes.',
  },
  {
    title: 'Document Automation',
    icon: '⚙',
    description:
      'End-to-end document generation and processing pipelines. From intake to output — drafting, citation, and formatting handled by AI with human oversight.',
  },
  {
    title: 'RAG & Legal Research',
    icon: '⟐',
    description:
      'Retrieval-augmented generation systems for instant legal research across large document sets. Hybrid vector + keyword search with multi-model orchestration.',
  },
  {
    title: 'Agentic Workflows',
    icon: '◈',
    description:
      'Multi-agent AI systems for complex business and legal processes. Task decomposition, autonomous execution, and evaluation loops that handle end-to-end workflows.',
  },
  {
    title: 'Custom AI Integration',
    icon: '△',
    description:
      'Full-stack integration of LLMs, embeddings, and ML models into existing business systems. API design, frontend interfaces, and deployment infrastructure.',
  },
  {
    title: 'Infrastructure & Deployment',
    icon: '▣',
    description:
      'Self-hosted server environments, distributed compute clusters, and GPU infrastructure. Scale when needed, save when idle — no unnecessary cloud spend.',
  },
];

const skills = {
  'Languages': ['Python', 'TypeScript', 'JavaScript', 'SQL', 'Rust', 'Solidity', 'PowerShell', 'ARM Assembly'],
  'Frameworks': ['FastAPI', 'Next.js', 'React', 'Node.js', 'Django', 'Flask'],
  'AI / ML': ['LLM Integration', 'RAG', 'Vector Search', 'Hybrid Retrieval', 'Embedding Models', 'Hugging Face', 'TensorFlow', 'Multi-model Orchestration'],
  'Infrastructure': ['Ubuntu Server Admin', 'Self-hosted Deployment', 'Distributed Computing', 'GPU Infra', 'REST APIs', 'WebSockets'],
  'Databases': ['MongoDB', 'SQL', 'Vector DBs', 'Supabase'],
  'Legal Tech': ['Contract Analysis', 'Document Automation', 'Trust Accounting', 'AGLC4 Citations', 'E-signature'],
};

const impactMetrics = [
  { value: 'Full stack', label: 'design to deployment' },
  { value: '4+', label: 'live production apps' },
  { value: 'Instant', label: 'retrieval response time' },
  { value: 'Law + AI', label: 'domain advantage' },
];

const Experience = () => {
  const [letterClass, setLetterClass] = useState('text-animate');
  const [scrollState, setScrollState] = useState({ progress: 0, scrollY: 0 });

  useEffect(() => {
    const timer = setTimeout(
      () => setLetterClass('text-animate-hover'),
      4000
    );
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const page = document.querySelector('.page');

    if (!page) {
      return undefined;
    }

    const handleScroll = () => {
      const maxScroll = Math.max(page.scrollHeight - page.clientHeight, 1);
      const scrollY = page.scrollTop;
      const progress = Math.min(scrollY / maxScroll, 1);

      setScrollState({ progress, scrollY });
    };

    handleScroll();
    page.addEventListener('scroll', handleScroll, { passive: true });

    return () => page.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div
      className="experience-page"
      style={{
        '--scroll-progress': `${scrollState.progress}`,
        '--parallax-soft': `${scrollState.scrollY * 0.08}px`,
        '--parallax-mid': `${scrollState.scrollY * 0.14}px`,
        '--parallax-strong': `${scrollState.scrollY * 0.2}px`,
      }}
    >
      <span className="tags exp-top-tag">&lt;body&gt;</span>

      <div className="exp-ambient exp-ambient--one" />
      <div className="exp-ambient exp-ambient--two" />
      <div className="exp-scroll-rail" aria-hidden="true">
        <span className="exp-scroll-rail__line" />
        <span className="exp-scroll-rail__glow" />
      </div>

      {/* Hero */}
      <section className="exp-hero">
        <h1>
          <AnimatedLetters
            letterClass={letterClass}
            strArray={'Experience'.split('')}
            idx={15}
          />
        </h1>
        <p className="exp-hero__summary">
          Legal Software Engineer combining Law and Commerce degrees with
          self-taught full-stack and AI engineering expertise. Specialising in
          production AI systems for legal workflows &mdash; contract analysis,
          retrieval-augmented generation, and legal research automation that
          deliver measurable business impact.
        </p>
        <div className="exp-hero__meta">
          <span>Sydney, Australia</span>
          <span>Macquarie University &mdash; Law &amp; Accounting</span>
          <span>
            <a href="https://github.com/Aaron-system" target="_blank" rel="noreferrer">GitHub</a>
            {' · '}
            <a href="https://www.linkedin.com/in/aaron-kreidieh-1383391ab/" target="_blank" rel="noreferrer">LinkedIn</a>
          </span>
        </div>
        <div className="exp-metrics">
          {impactMetrics.map((metric) => (
            <div key={metric.label} className="exp-metric-card">
              <strong>{metric.value}</strong>
              <span>{metric.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Career Timeline */}
      <section className="exp-section">
        <h2 className="exp-section__heading">
          <span className="exp-section__tag">&lt;career&gt;</span>
          Career Timeline
        </h2>
        <div className="timeline">
          {roles.map((role, i) => (
            <div key={role.company} className="timeline__item" style={{ animationDelay: `${0.3 + i * 0.2}s` }}>
              <div className="timeline__marker">
                <span className={`timeline__dot timeline__dot--${role.status === 'ACTIVE' ? 'active' : 'done'}`} />
                {i < roles.length - 1 && <span className="timeline__line" />}
              </div>
              <div className="timeline__card">
                <div className="timeline__card-header">
                  <div>
                    <h3>{role.title}</h3>
                    <p className="timeline__company">{role.company}</p>
                  </div>
                  <div className="timeline__right">
                    <span className={`timeline__status timeline__status--${role.status === 'ACTIVE' ? 'active' : 'done'}`}>
                      {role.status}
                    </span>
                    <span className="timeline__period">{role.period}</span>
                  </div>
                </div>
                <ul className="timeline__bullets">
                  {role.bullets.map((b, j) => (
                    <li key={j}>{b}</li>
                  ))}
                </ul>
                <div className="timeline__stack">
                  {role.stack.map((s) => (
                    <span key={s} className="tag">{s}</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Production Projects */}
      <section className="exp-section">
        <h2 className="exp-section__heading">
          <span className="exp-section__tag">&lt;production&gt;</span>
          Production Projects
        </h2>
        <div className="project-grid">
          {projects.map((p, i) => (
            <div key={p.title} className="project-card" style={{ animationDelay: `${0.2 + i * 0.15}s` }}>
              <div className="project-card__level">{p.level}</div>
              <h3>{p.title}</h3>
              <p>{p.description}</p>
              <div className="project-card__stack">
                {p.stack.map((s) => (
                  <span key={s} className="tag">{s}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* AI Workflow Services */}
      <section className="exp-section">
        <h2 className="exp-section__heading">
          <span className="exp-section__tag">&lt;services&gt;</span>
          AI Workflow Services
        </h2>
        <p className="services-intro">
          I build AI-powered workflow systems for law firms, legal teams, and
          businesses that need intelligent automation without the enterprise
          price tag. Every system is custom-built to your domain.
        </p>
        <div className="services-grid">
          {services.map((s, i) => (
            <div key={s.title} className="service-card" style={{ animationDelay: `${0.2 + i * 0.1}s` }}>
              <span className="service-card__icon">{s.icon}</span>
              <h3>{s.title}</h3>
              <p>{s.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Technical Skills */}
      <section className="exp-section">
        <h2 className="exp-section__heading">
          <span className="exp-section__tag">&lt;stack&gt;</span>
          Technical Skills
        </h2>
        <div className="skills-table">
          {Object.entries(skills).map(([category, items]) => (
            <div key={category} className="skills-row">
              <span className="skills-row__label">{category}</span>
              <div className="skills-row__items">
                {items.map((item) => (
                  <span key={item} className="tag">{item}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Education */}
      <section className="exp-section">
        <h2 className="exp-section__heading">
          <span className="exp-section__tag">&lt;education&gt;</span>
          Education
        </h2>
        <div className="education-block">
          <h3>Bachelor of Law and Accounting</h3>
          <p className="education-block__school">Macquarie University &mdash; Completed 2025</p>
          <p className="education-block__note">
            Self-taught full-stack developer since 2020. Built projects across
            web, AI, blockchain, and systems programming while completing
            dual degrees in law and commerce.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="exp-cta">
        <a
          href="/resume.docx"
          download="Aaron_Kreidieh_Resume_2026.docx"
          className="exp-cta__btn"
        >
          Download Resume
        </a>
        <Link to="/contact" className="exp-cta__btn exp-cta__btn--outline">
          Get in Touch
        </Link>
      </section>

      <div className="exp-footer-tag">
        <span>&lt;body&gt;</span>
        <br />
        <span>&lt;/body&gt;</span>
        <br />
        <span className="bottom-tag-html">&lt;/html&gt;</span>
      </div>
    </div>
  );
};

export default Experience;
